import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MultiSelect, OptionType } from "@/components/ui/multi-select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  PlusCircle,
  User,
  Mail,
  Phone,
  Lock,
  Image,
  Pencil,
  Trash2,
  Search,
  Loader2,
  Eye,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  userApi,
  accountApi,
  categoryApi,
  zoneApi,
  divisionApi,
  areaApi,
} from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { authenticatedApi, MEDIA_URL } from "@/services/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

// User type definition to match API response
type User = {
  id: string;
  organizationId: string;
  name: string;
  email: string;
  password?: string;
  image: string | null;
  mobile: string;
  whatsApp: string | null;
  fcm: string | null;
  lastLogin: string | null;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  cashAccounts?: Account[];
  bankAccounts?: Account[];
  categories?: Category[];
  zones?: Zone[];
  divisions?: Division[];
  areas?: Area[];
};

// Account type for dropdowns
type Account = {
  id: string;
  name: string;
  code: string;
  type: string;
  visibility: string;
  openingBalance: number;
  balanceType: string;
  status: boolean;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

// Category type for dropdowns
type Category = {
  id: string;
  name: string;
  code: string;
  status: boolean;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

// Zone type for dropdowns
type Zone = {
  id: string;
  name: string;
  code: string;
  status: boolean;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

// Division type for dropdowns
type Division = {
  id: string;
  name: string;
  code: string;
  status: boolean;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

// Area type for dropdowns
type Area = {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

// API response types
type ListResponse = {
  data: {
    docs: User[];
    totalDocs: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

type SingleResponse = {
  message: string;
  data: User;
};

type CreateUpdateResponse = {
  data: string;
  message: string;
};

type DeleteResponse = {
  message: string;
};

const ITEMS_PER_PAGE = 10;

// User roles
const USER_ROLES = ["owner", "collector", "accountant"];

// User role display names (for UI display)
const USER_ROLE_DISPLAY = {
  owner: "Owner",
  collector: "Collector",
  accountant: "Accountant",
};

// User status options
const USER_STATUSES = ["true", "false"];

// User status display names (for UI display)
const USER_STATUS_DISPLAY = {
  true: "Active",
  false: "Inactive",
};

const UsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    whatsApp: "",
    image: "",
    role: "collector",
    isActive: true,
    cashAccountIds: [] as string[],
    bankAccountIds: [] as string[],
    categoryIds: [] as string[],
    areaIds: [] as string[],
    zoneIds: [] as string[],
    divisionIds: [] as string[],
  });
  const [imagePreview, setImagePreview] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    query: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUserImagePreview, setShowUserImagePreview] = useState(false);
  const [userImageToPreview, setUserImageToPreview] = useState<string | null>(
    null
  );

  // Master data for dropdowns
  const [cashAccounts, setCashAccounts] = useState<Account[]>([]);
  const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);

  const { toast } = useToast();
  const { user } = useAuth();
  const location = useLocation();

  // Load users from API on initial render and when filters/pagination change
  useEffect(() => {
    fetchUsers();
  }, [currentPage, filters.query, filters.role, filters.status]);

  // Fetch master data for dropdowns
  useEffect(() => {
    fetchMasterData();
  }, []);

  // Check for filters in location state (for navigation from dashboard)
  useEffect(() => {
    if (location.state?.filters) {
      setFilters(prevFilters => ({
        ...prevFilters,
        ...location.state.filters
      }));
      // Clear the location state to prevent filters from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const params: {
        page?: number;
        limit?: number;
        query?: string;
        role?: string;
        status?: string;
      } = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      if (filters.query) params.query = filters.query;
      if (filters.role && filters.role !== "all") params.role = filters.role;
      if (filters.status && filters.status !== "all") {
        if (filters.status === "true") params.status = "true";
        else if (filters.status === "false") params.status = "false";
      }

      const response = await userApi.getUsers(params);

      setUsers(response.data.docs);
      setTotalDocs(response.data.totalDocs);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to fetch users",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch master data for dropdowns
  const fetchMasterData = async () => {
    try {
      setIsLoading(true);

      // Create an array of promises for all API calls
      const [
        cashAccountsResponse,
        bankAccountsResponse,
        categoriesResponse,
        zonesResponse,
        divisionsResponse,
        areasResponse,
      ] = await Promise.all([
        accountApi.getAccounts({ type: "cash", status: "true" }),
        accountApi.getAccounts({ type: "bank", status: "true" }),
        categoryApi.getCategories({ status: "true" }),
        zoneApi.getZones({ status: "true" }),
        divisionApi.getDivisions({ status: "true" }),
        areaApi.getAreas({}),
      ]);
      // Set state with the response data - handle both response structures
      setCashAccounts(
        cashAccountsResponse.docs || cashAccountsResponse.data?.docs || []
      );
      setBankAccounts(
        bankAccountsResponse.docs || bankAccountsResponse.data?.docs || []
      );
      setCategories(
        categoriesResponse.docs || categoriesResponse.data?.docs || []
      );
      setZones(zonesResponse.docs || zonesResponse.data?.docs || []);
      setDivisions(
        divisionsResponse.docs || divisionsResponse.data?.docs || []
      );
      setAreas(areasResponse.docs || areasResponse.data?.docs || []);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch master data for dropdowns",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    field: string,
    value: string | number | boolean | string[]
  ) => {
    // Don't trim during typing, only store the value as is
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle edit - fetch user details and populate form
  const handleEdit = async (userId: string) => {
    try {
      setIsLoading(true);
      const response = await userApi.getUser(userId);
      const userData = response.data;

      // Extract IDs from related entities
      const cashAccountIds =
        userData.cashAccounts?.map((account: Account) => account.id) || [];
      const bankAccountIds =
        userData.bankAccounts?.map((account: Account) => account.id) || [];
      const categoryIds =
        userData.categories?.map((category: Category) => category.id) || [];
      const zoneIds = userData.zones?.map((zone: Zone) => zone.id) || [];
      const divisionIds =
        userData.divisions?.map((division: Division) => division.id) || [];
      const areaIds = userData.areas?.map((area: Area) => area.id) || [];

      setFormData({
        name: userData.name.trim(),
        email: userData.email.trim(),
        password: userData.password || "", // Show current password
        mobile: userData.mobile.trim(),
        whatsApp: userData.whatsApp?.trim() || "",
        image: userData.image || "",
        role: userData.role,
        isActive: userData.isActive,
        cashAccountIds,
        bankAccountIds,
        categoryIds,
        zoneIds,
        divisionIds,
        areaIds,
      });

      if (userData.image) {
        setImagePreview(userData.image);
      }

      setEditingId(userId);
      setShowForm(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to fetch user details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete user
  const handleDelete = async (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsLoading(true);
      await userApi.deleteUser(userToDelete);

      fetchUsers(); // Refresh the list
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleAdd = async () => {
    // Validate form data
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      await userApi.createUser({
        ...formData,
        organizationId: user?.organizationId || "",
      });

      fetchUsers(); // Refresh the list
      resetForm();
      setShowForm(false);
      toast({
        title: "Success",
        description: "User added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to add user",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    // Validate form data
    if (!validateForm()) return;

    try {
      setIsLoading(true);

      // Create update payload (omit password if empty)
      const updatePayload = {
        ...formData,
        organizationId: user?.organizationId || "",
      };

      // Remove password if it's empty (not being updated)
      if (!formData.password) {
        delete updatePayload.password;
      }

      await userApi.updateUser(editingId!, updatePayload);

      fetchUsers(); // Refresh the list
      resetForm();
      setShowForm(false);
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Validation function
  const validateForm = () => {
    // Trim fields before validation
    const trimmedFormData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password.trim(),
      mobile: formData.mobile.trim(),
      whatsApp: formData.whatsApp.trim(),
    };

    // Validate required fields
    if (
      !trimmedFormData.name ||
      !trimmedFormData.email ||
      !trimmedFormData.mobile
    ) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedFormData.email)) {
      toast({
        title: "Validation Error",
        description:
          "Please enter a valid email address (e.g., user@example.com)",
        variant: "destructive",
      });
      return false;
    }

    // Validate mobile number (exactly 10 digits)
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(trimmedFormData.mobile)) {
      toast({
        title: "Validation Error",
        description: "Mobile number must be exactly 10 digits",
        variant: "destructive",
      });
      return false;
    }

    // Validate WhatsApp number (exactly 10 digits or empty)
    if (
      trimmedFormData.whatsApp &&
      !mobileRegex.test(trimmedFormData.whatsApp)
    ) {
      toast({
        title: "Validation Error",
        description: "WhatsApp number must be exactly 10 digits or empty",
        variant: "destructive",
      });
      return false;
    }

    // Validate password (minimum 6 characters, only for create or if provided during edit)
    if (!editingId || (editingId && trimmedFormData.password)) {
      if (trimmedFormData.password.length < 6) {
        toast({
          title: "Validation Error",
          description: "Password must be at least 6 characters long",
          variant: "destructive",
        });
        return false;
      }
    }

    // Validate collector fields if role is collector
    if (formData.role === "collector") {
      if (
        formData.categoryIds.length === 0 ||
        formData.areaIds.length === 0 ||
        formData.zoneIds.length === 0 ||
        formData.divisionIds.length === 0
      ) {
        toast({
          title: "Validation Error",
          description:
            "Please select at least one option for each collector field",
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      mobile: "",
      whatsApp: "",
      image: "",
      role: "collector",
      isActive: true,
      cashAccountIds: [],
      bankAccountIds: [],
      categoryIds: [],
      areaIds: [],
      zoneIds: [],
      divisionIds: [],
    });
    setImagePreview("");
    setEditingId(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      handleUpdate();
    } else {
      handleAdd();
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setIsLoading(true);
        const fileFormData = new FormData();
        fileFormData.append("files", file);
        const response = await authenticatedApi.uploadFiles(fileFormData);
        const imageUrl = response.data[0];
        console.log("imageUrl", imageUrl);
        setFormData({ ...formData, image: imageUrl });
        setImagePreview(`${MEDIA_URL}${imageUrl}`);
      } catch (error) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to upload image",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handler to allow only numeric input
  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Tab" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  };

  // Handle role change and clear collector fields when not collector
  const handleRoleChange = (role: string) => {
    setFormData((prev) => ({
      ...prev,
      role,
      // Clear collector fields if role is not collector
      ...(role !== "collector" && {
        categoryIds: [],
        areaIds: [],
        zoneIds: [],
        divisionIds: [],
      }),
    }));
  };

  // Format ISO date to DD/MM/YYYY HH:MM:SS
  const formatDateTime = (isoDate: string | null): string => {
    if (!isoDate) return "Never";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/,/, ""); // Converts "06/06/2025, 04:57:19" to "06/06/2025 04:57:19"
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalDocs / ITEMS_PER_PAGE);

  // Handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle modal close (including clicking outside)
  const handleModalClose = (open: boolean) => {
    if (!open) {
      resetForm();
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  };

  // Handle Add User button click
  const handleAddUserClick = () => {
    resetForm();
    setShowForm(true);
  };

  const handleView = async (user: User) => {
    try {
      setIsLoading(true);
      const response = await userApi.getUser(user.id);
      setViewUser(response.data);
      setShowViewModal(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to fetch user details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MastersLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name, email, mobile..."
                value={filters.query}
                onChange={(e) =>
                  handleFilterChange("query", e.target.value.trim())
                }
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Select
              value={filters.role}
              onValueChange={(value) => handleFilterChange("role", value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {USER_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {USER_ROLE_DISPLAY[
                      role as keyof typeof USER_ROLE_DISPLAY
                    ] || role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setFilters({ role: "all", status: "all", query: "" });
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* <Card>
          <CardHeader> */}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  Manage system users and their details
                </CardDescription>
              </div>
              <Button onClick={handleAddUserClick}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </div>
          {/* </CardHeader>
          <CardContent> */}
            <Table>
              <TableHeader>
                <TableRow className="h-10">
                  <TableHead className="whitespace-nowrap">Name</TableHead>
                  <TableHead className="whitespace-nowrap">Email</TableHead>
                  <TableHead className="whitespace-nowrap">Phone</TableHead>
                  <TableHead className="hidden md:table-cell whitespace-nowrap">Role</TableHead>
                  <TableHead className="hidden lg:table-cell whitespace-nowrap">
                    Last Login
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="w-[50px]">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-4">
                      <div className="flex justify-center items-center">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span>Loading...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-2 text-muted-foreground"
                    >
                      {filters.query || filters.role || filters.status
                        ? "No users match the selected filters"
                        : "No users found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="h-10">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 border"
                            onClick={() => {
                              if (user.image) {
                                setUserImageToPreview(
                                  `${MEDIA_URL}${user.image}`
                                );
                                setShowUserImagePreview(true);
                              }
                            }}
                            title="Click to enlarge"
                          >
                            <img
                              src={
                                user.image
                                  ? `${MEDIA_URL}${user.image}`
                                  : "/placeholder.svg"
                              }
                              alt={user.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {user.name}
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.mobile}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {USER_ROLE_DISPLAY[
                          user.role as keyof typeof USER_ROLE_DISPLAY
                        ] || user.role}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {formatDateTime(user.lastLogin)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`status-${user.id}`}
                            checked={user.isActive}
                            onCheckedChange={async (checked) => {
                              try {
                                setIsLoading(true);
                                await userApi.updateUser(user.id, {
                                  ...user,
                                  isActive: checked,
                                });
                                fetchUsers(); // Refresh the list
                                toast({
                                  title: "Success",
                                  description: `User status updated to ${
                                    checked ? "Active" : "Inactive"
                                  }`,
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description: error.message
                                    ? error.message
                                    : "Failed to update user status",
                                  variant: "destructive",
                                });
                              } finally {
                                setIsLoading(false);
                              }
                            }}
                          />
                          <span>{user.isActive ? "Active" : "Inactive"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(user)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(user.id)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(user.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          {/* </CardContent>
        </Card> */}
      </div>

      {showForm && (
        <Dialog open={showForm} onOpenChange={handleModalClose}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="top-0 bg-background z-10 pb-4 border-b">
              <DialogTitle>{editingId ? "Edit" : "Add New"} User</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update user details"
                  : "Create a new user account"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="image">Profile Image</Label>
                    <div className="flex items-center gap-4">
                      <div className="relative h-20 w-20 overflow-hidden rounded-full group bg-gray-100 flex items-center justify-center">
                        <img
                          src={
                            formData.image
                              ? `${MEDIA_URL}${formData.image}`
                              : "/placeholder.svg"
                          }
                          alt="Profile preview"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById("user-profile-image-input")
                              ?.click()
                          }
                          className="absolute bottom-1 right-1 bg-primary text-white rounded-full p-1 shadow-md border border-white flex items-center justify-center hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          style={{ width: 32, height: 32 }}
                          tabIndex={0}
                          aria-label="Upload profile image"
                        >
                          <span className="text-lg leading-none">+</span>
                        </button>
                        <input
                          id="user-profile-image-input"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      onKeyDown={handleNumericInput}
                      value={formData.mobile}
                      onChange={(e) =>
                        handleInputChange("mobile", e.target.value)
                      }
                      placeholder="Enter mobile number"
                      maxLength={10}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsApp">WhatsApp Number (Optional)</Label>
                    <Input
                      id="whatsApp"
                      type="tel"
                      onKeyDown={handleNumericInput}
                      value={formData.whatsApp}
                      onChange={(e) =>
                        handleInputChange("whatsApp", e.target.value)
                      }
                      placeholder="Enter WhatsApp number"
                      maxLength={10}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={handleRoleChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {USER_ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {USER_ROLE_DISPLAY[
                              role as keyof typeof USER_ROLE_DISPLAY
                            ] || role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="isActive">Status</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="isActive"
                        checked={formData.isActive}
                        onCheckedChange={(checked) =>
                          handleInputChange("isActive", checked)
                        }
                      />
                      <span>{formData.isActive ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{"Password"}</Label>
                    <Input
                      id="password"
                      type="string"
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      placeholder={"Enter password"}
                      required={!editingId}
                    />
                  </div>
                </div>

                {/* Account Selection Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Account Assignments</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cash Accounts</Label>
                      <MultiSelect
                        options={cashAccounts.map((account) => ({
                          label: account.name,
                          value: account.id,
                        }))}
                        selected={formData.cashAccountIds}
                        onChange={(selected) =>
                          handleInputChange("cashAccountIds", selected)
                        }
                        placeholder="Select cash accounts"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Bank Accounts</Label>
                      <MultiSelect
                        options={bankAccounts.map((account) => ({
                          label: account.name,
                          value: account.id,
                        }))}
                        selected={formData.bankAccountIds}
                        onChange={(selected) =>
                          handleInputChange("bankAccountIds", selected)
                        }
                        placeholder="Select bank accounts"
                      />
                    </div>
                  </div>
                </div>

                {/* Collector-specific fields */}
                {formData.role === "collector" && (
                  <div className="space-y-4 mt-4 border-t pt-4">
                    <h3 className="text-sm font-medium">
                      Collector Assignment
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Categories</Label>
                        <MultiSelect
                          options={categories.map((category) => ({
                            label: category.name,
                            value: category.id,
                          }))}
                          selected={formData.categoryIds}
                          onChange={(selected) =>
                            handleInputChange("categoryIds", selected)
                          }
                          placeholder="Select categories"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Areas</Label>
                        <MultiSelect
                          options={areas.map((area) => ({
                            label: area.name,
                            value: area.id,
                          }))}
                          selected={formData.areaIds}
                          onChange={(selected) =>
                            handleInputChange("areaIds", selected)
                          }
                          placeholder="Select areas"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Zones</Label>
                        <MultiSelect
                          options={zones.map((zone) => ({
                            label: zone.name,
                            value: zone.id,
                          }))}
                          selected={formData.zoneIds}
                          onChange={(selected) =>
                            handleInputChange("zoneIds", selected)
                          }
                          placeholder="Select zones"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Divisions</Label>
                        <MultiSelect
                          options={divisions.map((division) => ({
                            label: division.name,
                            value: division.id,
                          }))}
                          selected={formData.divisionIds}
                          onChange={(selected) =>
                            handleInputChange("divisionIds", selected)
                          }
                          placeholder="Select divisions"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Create"} User
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* View User Modal */}
      {showViewModal && viewUser && (
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
              <DialogDescription>
                View detailed information about the user
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Profile Image</Label>
                  <div className="flex items-center gap-4">
                    <div className="relative h-20 w-20 overflow-hidden rounded-full">
                      <img
                        src={
                          viewUser.image
                            ? `${MEDIA_URL}${viewUser.image}`
                            : "/placeholder.svg"
                        }
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <p className="text-sm">{viewUser.name}</p>
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <p className="text-sm">{viewUser.email}</p>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <p className="text-sm">{viewUser.mobile}</p>
                </div>

                <div className="space-y-2">
                  <Label>WhatsApp Number</Label>
                  <p className="text-sm">
                    {viewUser.whatsApp || "Not provided"}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <p className="text-sm">
                    {USER_ROLE_DISPLAY[
                      viewUser.role as keyof typeof USER_ROLE_DISPLAY
                    ] || viewUser.role}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <p className="text-sm">
                    {viewUser.isActive ? "Active" : "Inactive"}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Last Login</Label>
                  <p className="text-sm">
                    {formatDateTime(viewUser.lastLogin)}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Created At</Label>
                  <p className="text-sm">
                    {formatDateTime(viewUser.createdAt)}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Updated At</Label>
                  <p className="text-sm">
                    {formatDateTime(viewUser.updatedAt)}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Password</Label>
                  <p className="text-sm">
                    {viewUser.password || "No password set"}
                  </p>
                </div>
              </div>

              {/* Account Assignments Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Account Assignments</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cash Accounts</Label>
                    <div className="flex flex-wrap gap-1">
                      {viewUser.cashAccounts?.map((account) => (
                        <Badge key={account.id} variant="secondary">
                          {account.name}
                        </Badge>
                      ))}
                      {(!viewUser.cashAccounts ||
                        viewUser.cashAccounts.length === 0) && (
                        <span className="text-sm text-muted-foreground">
                          No cash accounts assigned
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bank Accounts</Label>
                    <div className="flex flex-wrap gap-1">
                      {viewUser.bankAccounts?.map((account) => (
                        <Badge key={account.id} variant="secondary">
                          {account.name}
                        </Badge>
                      ))}
                      {(!viewUser.bankAccounts ||
                        viewUser.bankAccounts.length === 0) && (
                        <span className="text-sm text-muted-foreground">
                          No bank accounts assigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Collector Assignments Section */}
              {viewUser.role === "collector" && (
                <div className="space-y-4 mt-4 border-t pt-4">
                  <h3 className="text-sm font-medium">Collector Assignments</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categories</Label>
                      <div className="flex flex-wrap gap-1">
                        {viewUser.categories?.map((category) => (
                          <Badge key={category.id} variant="secondary">
                            {category.name}
                          </Badge>
                        ))}
                        {(!viewUser.categories ||
                          viewUser.categories.length === 0) && (
                          <span className="text-sm text-muted-foreground">
                            No categories assigned
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Areas</Label>
                      <div className="flex flex-wrap gap-1">
                        {viewUser.areas?.map((area) => (
                          <Badge key={area.id} variant="secondary">
                            {area.name}
                          </Badge>
                        ))}
                        {(!viewUser.areas || viewUser.areas.length === 0) && (
                          <span className="text-sm text-muted-foreground">
                            No areas assigned
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Zones</Label>
                      <div className="flex flex-wrap gap-1">
                        {viewUser.zones?.map((zone) => (
                          <Badge key={zone.id} variant="secondary">
                            {zone.name}
                          </Badge>
                        ))}
                        {(!viewUser.zones || viewUser.zones.length === 0) && (
                          <span className="text-sm text-muted-foreground">
                            No zones assigned
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Divisions</Label>
                      <div className="flex flex-wrap gap-1">
                        {viewUser.divisions?.map((division) => (
                          <Badge key={division.id} variant="secondary">
                            {division.name}
                          </Badge>
                        ))}
                        {(!viewUser.divisions ||
                          viewUser.divisions.length === 0) && (
                          <span className="text-sm text-muted-foreground">
                            No divisions assigned
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              user and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Image Preview Dialog */}
      {showUserImagePreview && userImageToPreview && (
        <Dialog
          open={showUserImagePreview}
          onOpenChange={setShowUserImagePreview}
        >
          <DialogContent className="max-w-3xl">
            <div className="flex justify-center items-center p-4">
              <img
                src={userImageToPreview}
                alt="User Preview"
                className="max-w-full max-h-[80vh] object-contain bg-white rounded border"
                style={{ background: "#fff" }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </MastersLayout>
  );
};

export default UsersPage;
